import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AppRole =
  | "admin"
  | "super_admin"
  | "support"
  | "reviewer"
  | "successor"
  | "member"
  | string;

/**
 * Loads all roles for the given user from user_roles.
 * Used to decide whether to bypass member onboarding / UNA requirements.
 */
export const useUserRoles = (userId: string | undefined | null) => {
  const query = useQuery({
    queryKey: ["user-roles", userId],
    queryFn: async () => {
      if (!userId) return [] as AppRole[];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      if (error) {
        console.error("[useUserRoles]", error);
        return [] as AppRole[];
      }
      return (data ?? []).map((r: any) => r.role as AppRole);
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

  const roles = query.data ?? [];
  const isReviewer = roles.includes("reviewer");
  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const isSupport = roles.includes("support");
  // Users who should bypass member onboarding + UNA requirements entirely.
  const bypassMemberOnboarding = isReviewer || isAdmin || isSupport;

  // Preferred landing route for role-based users after login.
  const preferredHome = isReviewer
    ? "/review"
    : isAdmin
    ? "/admin/dashboard"
    : isSupport
    ? "/support"
    : null;

  return {
    roles,
    isReviewer,
    isAdmin,
    isSupport,
    bypassMemberOnboarding,
    preferredHome,
    isLoading: query.isLoading,
  };
};

/**
 * Non-hook helper: fetch roles once (used inside imperative handlers like
 * post-signIn navigation).
 */
export async function fetchUserRoles(userId: string): Promise<AppRole[]> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  return (data ?? []).map((r: any) => r.role as AppRole);
}

export function pickPreferredHome(roles: AppRole[]): string | null {
  if (roles.includes("reviewer")) return "/review";
  if (roles.includes("admin") || roles.includes("super_admin")) return "/admin/dashboard";
  if (roles.includes("support")) return "/support";
  return null;
}
